/**
 * Fait : l'écran de la notice — le sommaire à gauche, le chapitre lu à
 *   droite. Le chapitre courant se lit dans l'adresse, donc un chapitre se
 *   met en favori, se partage, et le bouton « précédent » du navigateur
 *   fonctionne.
 *
 * Ne fait pas : il n'écrit aucun contenu (il vit dans `chapitres/`) et ne
 *   parle à aucune API — la notice se lit hors ligne, et même déconnecté.
 */

import { useEffect, useRef } from 'react';

import { CHAPITRES, chapitrePourId } from './chapitres.ts';
import type { ChapitreDeNotice } from './contrat.ts';

/** Le sommaire. C'est une navigation, et il est annoncé comme telle. */
function Sommaire({ courant }: { readonly courant: string }) {
  return (
    <nav className="sommaire-notice" aria-label="Chapitres de la notice">
      <h2 className="etiquette-de-partie">Sommaire</h2>
      <ol>
        {CHAPITRES.map((chapitre, rang) => (
          <li key={chapitre.id}>
            <a
              href={`#/notice/${chapitre.id}`}
              data-chapitre={chapitre.id}
              // `aria-current` dit à voix haute où l'on se trouve : sans lui,
              // le repère visuel du chapitre courant n'existerait que pour
              // ceux qui voient l'écran.
              aria-current={chapitre.id === courant ? 'page' : undefined}
            >
              {/*
               * Le numéro est du TEXTE, pas un compteur CSS. Un `content:
               * counter(…)` s'affiche mais n'appartient pas au document : les
               * lecteurs d'écran ne le restituent pas de façon fiable, et
               * « chapitre 3 » est une information, pas un ornement.
               */}
              <span className="numero-de-chapitre">{rang + 1}.</span> {chapitre.titre}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

/** Un chapitre, tel qu'il se lit. */
function Chapitre({ chapitre }: { readonly chapitre: ChapitreDeNotice }) {
  const titre = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    // ON REMONTE EN HAUT à chaque changement de chapitre. Sans cela, la
    // position de défilement de l'ancien chapitre est conservée et l'on
    // atterrit au milieu du nouveau texte — insupportable à fort zoom, où l'on
    // ne voit qu'un fragment d'écran et où rien ne dit qu'on n'est pas au début.
    //
    // `instant` et non `smooth` : un défilement animé sur plusieurs écrans
    // donne le tournis, et la préférence « moins de mouvement » du système
    // doit être respectée partout.
    window.scrollTo({ top: 0, behavior: 'instant' });

    // ET on déplace le FOCUS sur le titre. Remonter le défilement ne déplace
    // pas le curseur du clavier : un lecteur d'écran resterait sur le lien du
    // menu et continuerait de lire l'ancien chapitre. Le focus sur le titre
    // fait annoncer le nouveau, et la lecture repart de là.
    titre.current?.focus();
  }, [chapitre.id]);

  return (
    <article className="chapitre-notice" data-chapitre={chapitre.id}>
      <header>
        {/* `tabIndex={-1}` rend le titre focalisable par le code sans l'ajouter
            au parcours du clavier : on n'a pas à tabuler dessus pour l'atteindre. */}
        <h2 ref={titre} tabIndex={-1}>
          {chapitre.titre}
        </h2>
        <p className="texte-attenue">{chapitre.resume}</p>
      </header>

      {chapitre.sections.map((section) => (
        <section key={section.titre}>
          <h3>{section.titre}</h3>

          {section.paragraphes.map((paragraphe) => (
            <p key={paragraphe.slice(0, 40)}>{paragraphe}</p>
          ))}

          {section.points !== undefined && (
            <dl className="points-de-notice">
              {section.points.map((point) => (
                <div key={point.quoi} className="point-de-notice">
                  <dt>{point.quoi}</dt>
                  <dd>
                    <p>{point.comment}</p>
                    {point.attention !== undefined && (
                      <p className="attention-de-notice">
                        <strong>À savoir :</strong> {point.attention}
                      </p>
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </section>
      ))}
    </article>
  );
}

export function PageNotice({ chapitreId }: { readonly chapitreId: string | null }) {
  const chapitre = chapitrePourId(chapitreId);

  return (
    <div className="notice" data-ecran="notice" data-chapitre-courant={chapitre.id}>
      <Sommaire courant={chapitre.id} />
      <Chapitre chapitre={chapitre} />
    </div>
  );
}
